import { useCallback, useEffect, useState } from "react";
import { proxy, useSnapshot } from "valtio";
import { useEditor } from "./useEditor";
import { useMessage } from "./useMessage";
import { useAreSpreadDesignsSupported } from "./useSpreadDesigns";
import { useAreTemplatesSupported } from "./useTemplates";
import { useDesigns } from "../components/bottom-designs-menus/use-designs";
import { useAppContext } from "./useAppContext";

function getHasFillableZones(definition: eXpress.core.IDefinition | null) {
	return {
		hasFillableTextZonesInDefinition: definition
			? definition.getFillableTextZones(false).length > 0
			: undefined,
		hasFillablePhotoZonesInDefinition: definition?.hasZoneForPhotoSourceType("photo"),
	} as const;
}

function getHasFillableZonesInSpread(spread: eXpress.core.Spread | undefined) {
	return {
		hasFillableTextZonesInSpread: spread
			? spread.getFillableTextZones(false).length > 0
			: undefined,
		hasFillablePhotoZonesInSpread: spread?.hasZoneForPhotoSourceType("photo"),
		hasFillableSymbolZonesInSpread: spread
			? spread.getFillableSymbolZones(false).length > 0
			: undefined,
	} as const;
}

function getAreBackgroundColorsSupported(definition: eXpress.core.IDefinition | null) {
	return definition?.product.showBackgroundColorTab;
}

function getAreBackgroundPatternsSupported(definition: eXpress.core.IDefinition | null) {
	return definition?.product.showBackgroundLibraryTab;
}

function getAreDesignsSupported(
	definition: eXpress.core.IDefinition | null,
	designs: readonly Readonly<eXpress.IAlternativeDesign>[] | undefined,
	isDesignsLoading: boolean,
) {
	if (!definition?.product.showDesigns) {
		return undefined;
	}
	if (isDesignsLoading) {
		return true;
	}
	return !!designs && designs.length > 1;
}

function getAreBackgroundsSupported(definition: eXpress.core.IDefinition | null) {
	return (
		getAreBackgroundColorsSupported(definition) || getAreBackgroundPatternsSupported(definition)
	);
}

function getAreSymbolsSupported(definition: eXpress.core.IDefinition | null) {
	return definition?.editor?.isSymbolEditingEnabled();
}

function isMapSupportedOnDefinition(
	definition: eXpress.core.IDefinition | null,
	context: eXpress.api.IServicesContext,
) {
	return (
		!!context.osmTileServerUrl &&
		!!context.osmMapboxAccessToken &&
		!!definition?.hasZoneForPhotoSourceType("map")
	);
}

interface IDefinitionFeaturesActions {
	updateFillableZones(definition: eXpress.core.IDefinition | null): void;
	updateBackgroundSupport(definition: eXpress.core.IDefinition | null): void;
	updateDesignsSupport(
		definition: eXpress.core.IDefinition | null,
		designs: readonly Readonly<eXpress.IAlternativeDesign>[] | undefined,
		isDesignsLoading: boolean,
	): void;
	updateSymbolsSupport(definition: eXpress.core.IDefinition | null): void;
	updateMapSupport(
		definition: eXpress.core.IDefinition | null,
		context: eXpress.api.IServicesContext,
	): void;
	updateTemplatesSupport(areSupported: boolean | undefined): void;
	updatePageDesignsSupport(areSupported: boolean | undefined): void;
	initializeAll(
		definition: eXpress.core.IDefinition | null,
		designs: readonly Readonly<eXpress.IAlternativeDesign>[] | undefined,
		context: eXpress.api.IServicesContext,
		isDesignsLoading: boolean,
	): void;
}

interface IDefinitionFeatures {
	hasFillableTextZonesInDefinition: boolean | undefined;
	hasFillablePhotoZonesInDefinition: boolean | undefined;
	areBackgroundsSupported: boolean | undefined;
	areBackgroundColorsSupported: boolean | undefined;
	areBackgroundPatternsSupported: boolean | undefined;
	areTemplatesSupported: boolean | undefined;
	areDesignsSupported: boolean | undefined;
	arePageDesignsSupported: boolean | undefined;
	areSymbolsSupported: boolean | undefined;
	isMapSupportedOnDefinition: boolean | undefined;
}

const definitionFeaturesState = proxy<
	IDefinitionFeatures & { actions: IDefinitionFeaturesActions }
>({
	hasFillableTextZonesInDefinition: undefined,
	hasFillablePhotoZonesInDefinition: undefined,
	areBackgroundsSupported: undefined,
	areBackgroundColorsSupported: undefined,
	areBackgroundPatternsSupported: undefined,
	areTemplatesSupported: undefined,
	areDesignsSupported: undefined,
	arePageDesignsSupported: undefined,
	areSymbolsSupported: undefined,
	isMapSupportedOnDefinition: undefined,
	actions: {
		updateFillableZones(definition: eXpress.core.IDefinition | null) {
			const { hasFillablePhotoZonesInDefinition, hasFillableTextZonesInDefinition } =
				getHasFillableZones(definition);
			definitionFeaturesState.hasFillablePhotoZonesInDefinition =
				hasFillablePhotoZonesInDefinition;
			definitionFeaturesState.hasFillableTextZonesInDefinition =
				hasFillableTextZonesInDefinition;
		},
		updateBackgroundSupport(definition: eXpress.core.IDefinition | null) {
			definitionFeaturesState.areBackgroundsSupported =
				getAreBackgroundsSupported(definition);
			definitionFeaturesState.areBackgroundColorsSupported =
				getAreBackgroundColorsSupported(definition);
			definitionFeaturesState.areBackgroundPatternsSupported =
				getAreBackgroundPatternsSupported(definition);
		},
		updateDesignsSupport(
			definition: eXpress.core.IDefinition | null,
			designs: readonly Readonly<eXpress.IAlternativeDesign>[] | undefined,
			isDesignsLoading: boolean,
		) {
			definitionFeaturesState.areDesignsSupported = getAreDesignsSupported(
				definition,
				designs,
				isDesignsLoading,
			);
		},
		updateSymbolsSupport(definition: eXpress.core.IDefinition | null) {
			definitionFeaturesState.areSymbolsSupported =
				getAreSymbolsSupported(definition) ?? false;
		},
		updateMapSupport(
			definition: eXpress.core.IDefinition | null,
			context: eXpress.api.IServicesContext,
		) {
			definitionFeaturesState.isMapSupportedOnDefinition = isMapSupportedOnDefinition(
				definition,
				context,
			);
		},
		updateTemplatesSupport(areSupported: boolean | undefined) {
			definitionFeaturesState.areTemplatesSupported = areSupported;
		},
		updatePageDesignsSupport(areSupported: boolean | undefined) {
			definitionFeaturesState.arePageDesignsSupported = areSupported;
		},
		initializeAll(
			definition: eXpress.core.IDefinition | null,
			designs: readonly Readonly<eXpress.IAlternativeDesign>[] | undefined,
			context: eXpress.api.IServicesContext,
			isDesignsLoading: boolean,
		) {
			definitionFeaturesState.actions.updateFillableZones(definition);
			definitionFeaturesState.actions.updateBackgroundSupport(definition);
			definitionFeaturesState.actions.updateDesignsSupport(
				definition,
				designs,
				isDesignsLoading,
			);
			definitionFeaturesState.actions.updateSymbolsSupport(definition);
			definitionFeaturesState.actions.updateMapSupport(definition, context);
		},
	},
});

/**
 * Detect and update features that are enabled for this Definition
 * Use this once to keep state up-to-date
 */
export function useSetDefinitionFeatures() {
	const editor = useEditor();
	const { context } = useAppContext();
	const { data: designs, isPending: isDesignsLoading } = useDesigns();

	// Get Template information
	const areTemplatesSupported = useAreTemplatesSupported();
	const arePageDesignsSupported = useAreSpreadDesignsSupported();

	// Update template support via actions
	useEffect(() => {
		definitionFeaturesState.actions.updateTemplatesSupport(areTemplatesSupported);
		definitionFeaturesState.actions.updatePageDesignsSupport(arePageDesignsSupported);
	}, [areTemplatesSupported, arePageDesignsSupported]);

	// Initialize
	useEffect(() => {
		definitionFeaturesState.actions.initializeAll(
			editor.definition,
			designs,
			context,
			isDesignsLoading,
		);
	}, [editor, designs, context, isDesignsLoading]);

	useMessage(
		useCallback(
			(message) => {
				const canZoneBeAddedOrRemoved = eXpress.core.canZoneBeAddedOrRemoved(message); // example: Apply templates

				// Can fillable zones in definition change
				if (canZoneBeAddedOrRemoved) {
					definitionFeaturesState.actions.updateFillableZones(editor.definition);
				}
				definitionFeaturesState.actions.updateDesignsSupport(
					editor.definition,
					designs,
					isDesignsLoading,
				);
				definitionFeaturesState.actions.updateBackgroundSupport(editor.definition);
				definitionFeaturesState.actions.updateSymbolsSupport(editor.definition);
				definitionFeaturesState.actions.updateMapSupport(editor.definition, context);
			},
			[editor, designs, context, isDesignsLoading],
		),
	);

	// Didn't return a value, this to reduce the chance of using it in multiple components
}

/**
 * Use to read the ser
 * Use this to read the values set by useSetDefinitionFeatures
 */
export function useDefinitionFeatures() {
	return useSnapshot(definitionFeaturesState);
}

/** Check which features are supported on the selected spread */
export function useSpreadFeatures() {
	const { selection } = useEditor();
	const [hasFillableZonesInSpread, setHasFillableZonesInSpread] = useState(() =>
		getHasFillableZonesInSpread(selection.getCurrentSpread()),
	);

	useMessage(
		useCallback(
			(message) => {
				const canZoneBeAddedOrRemoved = eXpress.core.canZoneBeAddedOrRemoved(message); // example: Apply templates
				const isSelectedSpreadChanged = eXpress.core.isSelectedSpreadChanged(message);

				// Can fillable Zones in spread change
				if (canZoneBeAddedOrRemoved || isSelectedSpreadChanged) {
					const spread = selection.getCurrentSpread();
					setHasFillableZonesInSpread(getHasFillableZonesInSpread(spread));
				}
			},
			[selection],
		),
	);

	return hasFillableZonesInSpread;
}
